/**
 * LessonProgressIndicator - Circular progress indicator for lesson completion
 * Requirements: 1.1, 1.2, 1.3, 5.1, 5.2, 5.3, 5.4
 *
 * Visual States:
 * - completed: Solid teal filled circle with white checkmark
 * - in_progress: Gray circle outline with teal arc stroke (percentage * 3.6°)
 * - not_started: Gray circle outline only
 */

import React from 'react';
import { useTheme, type LessonProgressStatus } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';

export interface LessonProgressIndicatorProps {
  /** Progress status */
  status: LessonProgressStatus;
  /** Completion percentage (0-100) for in_progress state */
  percentage?: number;
  /** Size in pixels (default: 20) */
  size?: number;
  /** Whether this lesson is currently active */
  isActive?: boolean;
}

/** Stroke width relative to size */
const STROKE_WIDTH_RATIO = 0.15;

/**
 * Component-specific values previously held in tokens.components.lessonProgressIndicator.
 * Inlined as part of the Phase 4 component-token flatten — these are bespoke layout
 * values (not part of the design-system token set).
 */
const PROGRESS_TRACK_COLOR = '#BFC4D0';
const PROGRESS_ACTIVE_GLOW = '0 0 6px rgba(91, 142, 255, 0.35)';

/**
 * Calculate SVG arc path for a given percentage
 * Arc starts at top (12 o'clock) and goes clockwise
 */
function describeArc(
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number
): string {
  // Convert angles from degrees to radians
  // Subtract 90 to start from top (12 o'clock position)
  const startRad = ((startAngle - 90) * Math.PI) / 180;
  const endRad = ((endAngle - 90) * Math.PI) / 180;

  const startX = cx + radius * Math.cos(startRad);
  const startY = cy + radius * Math.sin(startRad);
  const endX = cx + radius * Math.cos(endRad);
  const endY = cy + radius * Math.sin(endRad);

  // Large arc flag: 1 if angle > 180 degrees
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

  return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
}

/**
 * LessonProgressIndicator component
 *
 * Renders a circular progress indicator showing lesson completion state.
 * Requirements:
 * - 1.1: Filled circle with checkmark for completed
 * - 1.2: Empty gray circle for not_started
 * - 1.3: Gray circle with teal arc for in_progress
 * - 5.1: Solid filled circle with Highlight_Color and white checkmark
 * - 5.2: Gray circle outline with Highlight_Color arc proportional to completion
 * - 5.3: Gray circle outline with no fill for unstarted
 * - 5.4: Arc angle = (completed_blocks / total_blocks) * 360 degrees
 */
export const LessonProgressIndicator: React.FC<LessonProgressIndicatorProps> = ({
  status,
  percentage = 0,
  size = 20,
  isActive = false,
}) => {
  const { tokens } = useTheme();
  const { t } = useTranslation();
  const strokeWidth = Math.max(2, size * STROKE_WIDTH_RATIO);
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;

  // Clamp percentage to valid range
  const clampedPercentage = Math.max(0, Math.min(100, percentage));
  // Calculate arc angle: percentage * 3.6 (since 100% = 360°)
  const arcAngle = clampedPercentage * 3.6;

  const renderCompleted = () => (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-label={t('lesson_progress.completed')}
      role="img"
      data-testid="progress-indicator-completed"
    >
      {/* Filled teal circle */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill={tokens.choice.drag.border}
        stroke="none"
      />
      {/* White checkmark */}
      <path
        d={`M ${size * 0.28} ${size * 0.5} L ${size * 0.42} ${size * 0.65} L ${size * 0.72} ${size * 0.35}`}
        stroke={tokens.text.onInverse}
        strokeWidth={strokeWidth * 0.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );

  const renderInProgress = () => (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-label={t('lesson_progress.in_progress', { percentage: Math.round(clampedPercentage) })}
      role="img"
      data-testid="progress-indicator-in-progress"
    >
      {/* Gray circle outline (background) */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={PROGRESS_TRACK_COLOR}
        strokeWidth={strokeWidth}
      />
      {/* Teal arc showing progress */}
      {arcAngle > 0 && (
        <path
          d={describeArc(center, center, radius, 0, arcAngle)}
          fill="none"
          stroke={tokens.choice.drag.border}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
      )}
    </svg>
  );

  const renderNotStarted = () => (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-label={t('lesson_progress.not_started')}
      role="img"
      data-testid="progress-indicator-not-started"
    >
      {/* Gray circle outline only */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={PROGRESS_TRACK_COLOR}
        strokeWidth={strokeWidth}
      />
    </svg>
  );

  // Add active indicator styling if needed
  const wrapperStyle: React.CSSProperties = isActive
    ? { filter: `drop-shadow(${PROGRESS_ACTIVE_GLOW})` }
    : {};

  return (
    <span
      className="lesson-progress-indicator"
      style={wrapperStyle}
      data-status={status}
      data-active={isActive}
    >
      {status === 'completed' && renderCompleted()}
      {status === 'in_progress' && renderInProgress()}
      {status === 'not_started' && renderNotStarted()}
    </span>
  );
};
